import * as React from 'react'
import { connect } from 'react-redux'
import { match } from 'react-router-dom'

import { IRootState } from '../../../common/reducers'
import { IQuery, ICommonLocation } from '../../../common/utils/location/types'
import { IPageComponent } from '../../../typings/pageComponent'
import { IRouteConfigComponentProps } from '../../../typings/routes'

type IOwnProps = IRouteConfigComponentProps<IQuery>

interface IStateProps {
  ProxiedComponent: IPageComponent<IOwnProps>
}

type Props = IStateProps & IOwnProps

const getRootStateWithUpdatedLocation = (
  state: IRootState,
  location: ICommonLocation
): IRootState => ({
  ...state,
  router: {
    ...state.router,
    location: {
      state: undefined,
      ...location
    }
  }
})

export type MapStateToPageComponent = (
  state: IRootState,
  routeMatch: match<any>
) => IPageComponent<IOwnProps>

const PageProxyComponent = (props: Props) => {
  const { ProxiedComponent, ...proxiedProps } = props
  return <ProxiedComponent {...proxiedProps} />
}

const PageProxyContainer = (
  mapStateToPageComponent: MapStateToPageComponent
) => {
  const mapStateToProps = (
    state: IRootState,
    ownProps: IOwnProps
  ): IStateProps => ({
    ProxiedComponent: mapStateToPageComponent(state, ownProps.match)
  })

  const PageProxyContainer: IPageComponent<IOwnProps> = connect<
    IStateProps,
    void,
    IOwnProps,
    IRootState
  >(mapStateToProps)(PageProxyComponent)

  PageProxyContainer.loadData = props => {
    /* 
      For first render, `location` in redux store is not up to date
      when `.loadData` is called as it is updated later in the data load flow.
      To overcome this, `patchedState` has updated `location` for the use of
      `PageProxyContainer` only.
    */
    const patchedState = getRootStateWithUpdatedLocation(
      props.store.getState(),
      props.location
    )
    const ProxiedComponent = mapStateToPageComponent(
      patchedState,
      props.routeMatch
    )
    if (ProxiedComponent.loadData) {
      return ProxiedComponent.loadData(props)
    } else {
      return Promise.resolve()
    }
  }

  PageProxyContainer.onBeforePageView = async ({
    store,
    location,
    routeMatch,
    prevLocation
  }) => {
    const ProxiedComponent = mapStateToPageComponent(
      store.getState(),
      routeMatch
    )
    if (ProxiedComponent.onBeforePageView) {
      return ProxiedComponent.onBeforePageView({
        store,
        location,
        routeMatch,
        prevLocation
      })
    }
    return Promise.resolve()
  }

  return PageProxyContainer
}

export default PageProxyContainer
